import React, { FC } from 'react';

import { Box, Center, HStack, Stack } from '@chakra-ui/react';
import { sum, values } from 'lodash';
import { useSelector } from 'react-redux';
import { customTheme } from 'theme';

import { CustomSpinner } from 'components/DataDisplay/Spinner/CustomSpinner';
import formatNumber, {
  formatNumberWithComma,
} from 'components/DataDisplay/Utils/formatNumber';
import { BarProgress } from 'components/Visualization/Progress/BarProgress';
import { selectCompliance } from 'containers/Compliance/selectors';

export const complianceSummary = (label, value, isLoading) => (
  <Center w="full" h="full" pt={4} pb={5}>
    <Stack justify="space-between" h="full">
      <Center>
        <Box fontWeight={600}>{label}</Box>
      </Center>
      <Center fontSize="2xl">
        {isLoading ? <CustomSpinner /> : value ? value : 0}
      </Center>
    </Stack>
  </Center>
);

export const TotalEntities: FC = () => {
  const { summary, blueprints } = useSelector(selectCompliance);
  return complianceSummary(
    'Entities Evaluated',
    formatNumber(summary.data?.total_entities, 1),
    summary.isLoading || blueprints.isLoading,
  );
};

export const Policies: FC = () => {
  const { summary, blueprints } = useSelector(selectCompliance);
  return complianceSummary(
    'Total Policies',
    formatNumber(summary.data?.total_policies, 1),
    summary.isLoading || blueprints.isLoading,
  );
};

export const renderPassedChecksSummary = (passed, total, percent) => (
  <Center w="full" px={4} pt={4} pb={3}>
    <Stack w="full" justify="space-between" h="full" spacing={0}>
      <Box fontWeight={600}>Passed Policy Checks</Box>

      <HStack w="full">
        <Stack spacing={0} w="full">
          <Box fontSize="sm" color={customTheme.colors.gray[300]}>
            {formatNumberWithComma(passed)} out of{' '}
            {formatNumberWithComma(total)}
          </Box>
          <Box w="full" pb={2}>
            <BarProgress value={passed} total={total} h={3.5} />
          </Box>
        </Stack>

        <Box fontWeight={200} fontSize="3xl" whiteSpace="nowrap" pt={3}>
          {percent}%
        </Box>
      </HStack>
    </Stack>
  </Center>
);

export const PassedChecks: FC = () => {
  const { summary } = useSelector(selectCompliance);
  const passed = sum(values(summary.data?.check_stats?.passed_details));
  const failed = sum(values(summary.data?.check_stats?.failed));
  const percent = !passed ? 0 : ((passed / (passed + failed)) * 100).toFixed(0);

  return renderPassedChecksSummary(passed, passed + failed, percent);
};

export const PolicyExceptions: FC = () => {
  const { summary, blueprints } = useSelector(selectCompliance);
  return complianceSummary(
    'Policy Exceptions',
    formatNumber(summary.data?.check_stats?.rule_exceptions, 1),
    summary.isLoading || blueprints.isLoading,
  );
};

export const ResourceExceptions: FC = () => {
  const { summary, blueprints } = useSelector(selectCompliance);
  return complianceSummary(
    'Resource Exceptions',
    formatNumber(summary.data?.check_stats?.resource_exceptions, 1),
    summary.isLoading || blueprints.isLoading,
  );
};
